import React, { useState, useEffect } from 'react';
import { ReferenceData, submitReferences } from '../../api/onboardingService';

interface ReferencesStepProps {
  data?: any[];
  onComplete: (data: any[]) => void;
  onNext: () => void;
  onPrevious: () => void;
}

const ReferencesStep: React.FC<ReferencesStepProps> = ({ data, onComplete, onNext, onPrevious }) => {
  const [references, setReferences] = useState<ReferenceData[]>([
    {
      name: '',
      email: '',
      phone: '',
      relationshipType: 'pastor',
      relationshipDescription: '',
      yearsKnown: '',
      organization: '',
      position: '',
    },
    {
      name: '',
      email: '',
      phone: '',
      relationshipType: 'employer',
      relationshipDescription: '',
      yearsKnown: '',
      organization: '',
      position: '',
    },
    {
      name: '',
      email: '',
      phone: '',
      relationshipType: 'friend',
      relationshipDescription: '',
      yearsKnown: '',
      organization: '',
      position: '',
    },
  ]);

  const [errors, setErrors] = useState<Record<string, Record<string, string>>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [currentReferenceIndex, setCurrentReferenceIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const referenceTypes = [
    { value: 'pastor', label: 'Pastor/Minister' },
    { value: 'employer', label: 'Employer/Supervisor' },
    { value: 'friend', label: 'Friend' },
    { value: 'mentor', label: 'Mentor' },
    { value: 'colleague', label: 'Colleague' },
    { value: 'other', label: 'Other' },
  ];

  useEffect(() => {
    if (data && data.length > 0) {
      const defaults: ReferenceData[] = [
        {
          name: '',
          email: '',
          phone: '',
          relationshipType: 'pastor',
          relationshipDescription: '',
          yearsKnown: '',
          organization: '',
          position: '',
        },
        {
          name: '',
          email: '',
          phone: '',
          relationshipType: 'employer',
          relationshipDescription: '',
          yearsKnown: '',
          organization: '',
          position: '',
        },
        {
          name: '',
          email: '',
          phone: '',
          relationshipType: 'friend',
          relationshipDescription: '',
          yearsKnown: '',
          organization: '',
          position: '',
        },
      ];

      const merged = defaults.map((defaultRef, index) => ({
        ...defaultRef,
        ...(data[index] || {}),
      }));

      if (data.length > defaults.length) {
        merged.push(...data.slice(defaults.length));
      }

      setReferences(merged);
    }
  }, [data]);

  // Listen for navigation events from sidebar
  useEffect(() => {
    const handleNavigateToSection = (event: CustomEvent) => {
      const sectionIndex = event.detail;
      if (sectionIndex >= 0 && sectionIndex < references.length) {
        setCurrentReferenceIndex(sectionIndex);
        // Notify parent component to update sidebar highlight
        const updateEvent = new CustomEvent('subsectionChanged', { detail: sectionIndex });
        window.dispatchEvent(updateEvent);
        window.scrollTo(0, 0);
      }
    };

    window.addEventListener('navigateToSection' as any, handleNavigateToSection as EventListener);
    return () => {
      window.removeEventListener('navigateToSection' as any, handleNavigateToSection as EventListener);
    };
  }, [references.length]);

  // Notify parent when section changes internally (via next/prev buttons)
  useEffect(() => {
    const updateEvent = new CustomEvent('subsectionChanged', { detail: currentReferenceIndex });
    window.dispatchEvent(updateEvent);
  }, [currentReferenceIndex]);

  // Check if a reference is complete (all required fields have content)
  const isReferenceComplete = (index: number): boolean => {
    const ref = references[index];
    if (!ref) return false;
    
    // Required fields: name, email, relationshipType, relationshipDescription
    return !!(
      ref.name?.trim() &&
      ref.email?.trim() &&
      ref.relationshipType &&
      ref.relationshipDescription?.trim()
    );
  };

  // Calculate completion status for all references
  const getReferenceCompletionStatus = (): boolean[] => {
    return references.map((_, index) => isReferenceComplete(index));
  };

  // Notify parent of completion status and reference types whenever references change
  useEffect(() => {
    const completionStatus = getReferenceCompletionStatus();
    const referenceLabels = references.map(ref => {
      const type = referenceTypes.find(rt => rt.value === ref.relationshipType);
      return type ? type.label : ref.relationshipType;
    });
    const updateEvent = new CustomEvent('subsectionCompletionChanged', { 
      detail: { 
        completions: completionStatus,
        referenceTypes: referenceLabels
      }
    });
    window.dispatchEvent(updateEvent);
  }, [references, referenceTypes]);

  const validateCurrentReference = () => {
    const ref = references[currentReferenceIndex];
    const refErrors: Record<string, string> = {};

    if (!ref.name.trim()) {
      refErrors.name = 'Name is required';
    }

    if (!ref.email.trim()) {
      refErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ref.email)) {
      refErrors.email = 'Please enter a valid email address';
    }

    if (!ref.relationshipType) {
      refErrors.relationshipType = 'Relationship type is required';
    }

    if (!ref.relationshipDescription || !ref.relationshipDescription.trim()) {
      refErrors.relationshipDescription = 'Relationship description is required';
    }

    if (ref.yearsKnown && (ref.yearsKnown < 0 || ref.yearsKnown > 50)) {
      refErrors.yearsKnown = 'Years known must be between 0 and 50';
    }

    setErrors({ [currentReferenceIndex]: refErrors });
    return Object.keys(refErrors).length === 0;
  };

  const handleInputChange = (index: number, field: keyof ReferenceData, value: string | number) => {
    const newReferences = [...references];
    newReferences[index] = {
      ...newReferences[index],
      [field]: value,
    };
    setReferences(newReferences);

    // Clear error when user starts typing
    if (errors[index]?.[field]) {
      const newErrors = { ...errors };
      if (newErrors[index]) {
        delete newErrors[index][field];
        if (Object.keys(newErrors[index]).length === 0) {
          delete newErrors[index];
        }
      }
      setErrors(newErrors);
    }

    // Auto-save draft after a short delay
    autoSaveDraft(newReferences);
  };

  // Debounced auto-save for current reference
  const autoSaveTimer = React.useRef<NodeJS.Timeout | null>(null);
  const autoSaveDraft = async (updatedReferences: ReferenceData[]) => {
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }

    autoSaveTimer.current = setTimeout(async () => {
      try {
        setIsSaving(true);
        
        // Only submit references that are fully complete
        const filteredReferences = updatedReferences.map(ref => {
          const filtered: any = {};
          
          // Only include fields that have actual values
          if (ref.name?.trim()) filtered.name = ref.name.trim();
          if (ref.email?.trim()) filtered.email = ref.email.trim();
          if (ref.phone?.trim()) filtered.phone = ref.phone.trim();
          if (ref.relationshipType) filtered.relationshipType = ref.relationshipType;
          if (ref.relationshipDescription?.trim()) filtered.relationshipDescription = ref.relationshipDescription.trim();
          // yearsKnown can be a number or string, handle both cases
          if (typeof ref.yearsKnown === 'number') {
            filtered.yearsKnown = ref.yearsKnown;
          } else if (typeof ref.yearsKnown === 'string') {
            const trimmedYearsKnown = ref.yearsKnown.trim();
            if (trimmedYearsKnown) {
              filtered.yearsKnown = trimmedYearsKnown;
            }
          }
          if (ref.organization?.trim()) filtered.organization = ref.organization.trim();
          if (ref.position?.trim()) filtered.position = ref.position.trim();
          
          // Only include reference if it is fully complete
          if (
            filtered.name &&
            filtered.email &&
            filtered.relationshipType &&
            filtered.relationshipDescription
          ) {
            return filtered;
          }
          return null;
        }).filter(ref => ref !== null); // Remove null entries
        
        // Only save if we have at least one reference with data
        if (filteredReferences.length > 0) {
          await submitReferences(filteredReferences);
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 2000);
        }
      } catch (error) {
        console.error('Error auto-saving reference:', error);
      } finally {
        setIsSaving(false);
      }
    }, 1000);
  };

  const handleNextReference = async () => {
    if (validateCurrentReference()) {
      // Save current progress before moving to next reference
      setIsSaving(true);
      setSaveError(null);
      setSaveSuccess(false);
      
      try {
        const completedReferences = references.filter((_, index) => isReferenceComplete(index));
        await submitReferences(completedReferences);
        setSaveSuccess(true);
        
        // Brief delay to show success message, then proceed
        setTimeout(() => {
          if (currentReferenceIndex < references.length - 1) {
            setCurrentReferenceIndex(currentReferenceIndex + 1);
            setSaveSuccess(false);
            window.scrollTo(0, 0);
            setIsSaving(false);
          } else {
            // All references complete - call onComplete and onNext
            setSaveSuccess(false);
            setIsSaving(false);
            try {
              onComplete(references);
              onNext();
            } catch (error) {
              console.error('Error completing references:', error);
              setSaveError('Failed to complete references. Please try again.');
            }
          }
        }, 800);
      } catch (error) {
        console.error('Error saving reference:', error);
        setSaveError('Failed to save your progress. Please try again.');
        setIsSaving(false);
      }
    }
  };

  const handlePreviousReference = () => {
    if (currentReferenceIndex > 0) {
      setCurrentReferenceIndex(currentReferenceIndex - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all references
    const allValid = references.every((ref, index) => {
      setCurrentReferenceIndex(index);
      return validateCurrentReference();
    });
    
    if (!allValid) {
      // Find first invalid reference
      const firstInvalid = references.findIndex((ref, index) => !isReferenceComplete(index));
      if (firstInvalid >= 0) {
        setCurrentReferenceIndex(firstInvalid);
        window.scrollTo(0, 0);
      }
      return;
    }

    setIsLoading(true);
    
    try {
      await submitReferences(references);
      await onComplete(references);
    } catch (error) {
      console.error('Error saving references data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const currentReference = references[currentReferenceIndex];
  const progress = ((currentReferenceIndex + 1) / references.length) * 100;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">References</h2>
        <p className="text-gray-600">
          Please provide three references who can speak to your character, ministry experience, and calling to church planting. 
          We will contact these individuals as part of your application process.
        </p>
        <div className="mt-4 p-4 bg-yellow-50 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Important:</strong> Please inform your references that they will be contacted by 242Network staff. 
                Make sure their contact information is current and accurate.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">
            Reference {currentReferenceIndex + 1} of {references.length}
          </span>
          {saveSuccess && (
            <span className="sr-only">Progress saved</span>
          )}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Save Success Message */}
      {saveSuccess && (
        <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-full border border-green-200 bg-white px-3 py-2 shadow-sm">
          <svg className="h-4 w-4 text-green-600" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span className="text-xs font-medium text-green-700">Saved</span>
        </div>
      )}

      {/* Save Error Message */}
      {saveError && (
        <div className="mb-6 p-4 bg-red-50 rounded-md border border-red-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{saveError}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Reference {currentReferenceIndex + 1}
            </h3>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
              {referenceTypes.find(rt => rt.value === currentReference.relationshipType)?.label}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor={`name-${currentReferenceIndex}`} className="block text-sm font-medium text-gray-700">
                Full Name *
              </label>
              <input
                type="text"
                id={`name-${currentReferenceIndex}`}
                value={currentReference.name}
                onChange={(e) => handleInputChange(currentReferenceIndex, 'name', e.target.value)}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                  errors[currentReferenceIndex]?.name ? 'border-red-300' : ''
                }`}
                placeholder="Full name"
              />
              {errors[currentReferenceIndex]?.name && <p className="mt-1 text-sm text-red-600">{errors[currentReferenceIndex].name}</p>}
            </div>

            <div>
              <label htmlFor={`email-${currentReferenceIndex}`} className="block text-sm font-medium text-gray-700">
                Email Address *
              </label>
              <input
                type="email"
                id={`email-${currentReferenceIndex}`}
                value={currentReference.email}
                onChange={(e) => handleInputChange(currentReferenceIndex, 'email', e.target.value)}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                  errors[currentReferenceIndex]?.email ? 'border-red-300' : ''
                }`}
                placeholder="Email address"
              />
              {errors[currentReferenceIndex]?.email && <p className="mt-1 text-sm text-red-600">{errors[currentReferenceIndex].email}</p>}
            </div>

            <div>
              <label htmlFor={`phone-${currentReferenceIndex}`} className="block text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <input
                type="tel"
                id={`phone-${currentReferenceIndex}`}
                value={currentReference.phone}
                onChange={(e) => handleInputChange(currentReferenceIndex, 'phone', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Phone number"
              />
            </div>

            <div>
              <label htmlFor={`relationshipType-${currentReferenceIndex}`} className="block text-sm font-medium text-gray-700">
                Relationship Type *
              </label>
              <select
                id={`relationshipType-${currentReferenceIndex}`}
                value={currentReference.relationshipType}
                onChange={(e) => handleInputChange(currentReferenceIndex, 'relationshipType', e.target.value as any)}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                  errors[currentReferenceIndex]?.relationshipType ? 'border-red-300' : ''
                }`}
              >
                {referenceTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {errors[currentReferenceIndex]?.relationshipType && <p className="mt-1 text-sm text-red-600">{errors[currentReferenceIndex].relationshipType}</p>}
            </div>

            <div>
              <label htmlFor={`yearsKnown-${currentReferenceIndex}`} className="block text-sm font-medium text-gray-700">
                Years Known
              </label>
              <input
                type="number"
                id={`yearsKnown-${currentReferenceIndex}`}
                min="0"
                max="50"
                value={currentReference.yearsKnown}
                onChange={(e) => handleInputChange(currentReferenceIndex, 'yearsKnown', e.target.value ? parseInt(e.target.value) : '')}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                  errors[currentReferenceIndex]?.yearsKnown ? 'border-red-300' : ''
                }`}
                placeholder="Years"
              />
              {errors[currentReferenceIndex]?.yearsKnown && <p className="mt-1 text-sm text-red-600">{errors[currentReferenceIndex].yearsKnown}</p>}
            </div>

            <div>
              <label htmlFor={`organization-${currentReferenceIndex}`} className="block text-sm font-medium text-gray-700">
                Organization/Church
              </label>
              <input
                type="text"
                id={`organization-${currentReferenceIndex}`}
                value={currentReference.organization}
                onChange={(e) => handleInputChange(currentReferenceIndex, 'organization', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Organization or church name"
              />
            </div>

            <div className="sm:col-span-2">
              <label htmlFor={`position-${currentReferenceIndex}`} className="block text-sm font-medium text-gray-700">
                Position/Title
              </label>
              <input
                type="text"
                id={`position-${currentReferenceIndex}`}
                value={currentReference.position}
                onChange={(e) => handleInputChange(currentReferenceIndex, 'position', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Position or title"
              />
            </div>

            <div className="sm:col-span-2">
              <label htmlFor={`relationshipDescription-${currentReferenceIndex}`} className="block text-sm font-medium text-gray-700">
                Relationship Description *
              </label>
              <textarea
                id={`relationshipDescription-${currentReferenceIndex}`}
                rows={3}
                value={currentReference.relationshipDescription}
                onChange={(e) => handleInputChange(currentReferenceIndex, 'relationshipDescription', e.target.value)}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                  errors[currentReferenceIndex]?.relationshipDescription ? 'border-red-300' : ''
                }`}
                placeholder="Describe your relationship with this person and how they know you..."
              />
              {errors[currentReferenceIndex]?.relationshipDescription && <p className="mt-1 text-sm text-red-600">{errors[currentReferenceIndex].relationshipDescription}</p>}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-6 border-t border-gray-200 mt-8">
          <button
            type="button"
            onClick={currentReferenceIndex === 0 ? onPrevious : handlePreviousReference}
            disabled={isLoading || isSaving}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {currentReferenceIndex === 0 ? 'Previous Step' : 'Previous Reference'}
          </button>

          <button
            type="button"
            onClick={handleNextReference}
            disabled={isLoading || isSaving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : currentReferenceIndex < references.length - 1 ? (
              <>
                Save & Continue to Reference {currentReferenceIndex + 2}
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </>
            ) : (
              <>
                Save & Complete All References
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReferencesStep;
